/**
 * Tabla de roles (presentacional).
 *
 * Réplica de `roles.html` del SSR (`progress/explore_equipo_ssr.md`, §B.6): una
 * sección por tipo (sistema / personalizados) con ID, nombre, descripción, un
 * resumen de permisos y acciones (Editar; Eliminar solo en personalizados). Sin
 * lógica de datos: recibe los roles y notifica las acciones al padre.
 */
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";

import type { Role } from "../types";

interface RolesTableProps {
  /** Título de la sección (p.ej. "Roles del sistema"). */
  title: string;
  roles: Role[];
  /** Si la sección permite eliminar (solo personalizados). */
  allowDelete: boolean;
  /** Texto cuando no hay roles en la sección. */
  emptyText: string;
  onEdit: (role: Role) => void;
  onDelete?: (role: Role) => void;
}

/** Resume los permisos de un rol: "all" o "N permisos" (o "Sin permisos"). */
function permissionsSummary(role: Role): string {
  if (role.permissions.includes("all")) {
    return "Acceso completo";
  }
  const count = role.permissions.length;
  if (count === 0) {
    return "Sin permisos";
  }
  return count === 1 ? "1 permiso" : `${count} permisos`;
}

export function RolesTable({
  title,
  roles,
  allowDelete,
  emptyText,
  onEdit,
  onDelete,
}: RolesTableProps) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {roles.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-slate-500">{emptyText}</p>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>ID</TableHeaderCell>
              <TableHeaderCell>Nombre</TableHeaderCell>
              <TableHeaderCell>Descripción</TableHeaderCell>
              <TableHeaderCell>Permisos</TableHeaderCell>
              <TableHeaderCell className="text-right">Acciones</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell>
                  <span className="font-mono text-xs text-slate-700">{role.id}</span>
                </TableCell>
                <TableCell className="font-medium text-slate-900">{role.name}</TableCell>
                <TableCell className="text-slate-500">{role.description || "—"}</TableCell>
                <TableCell>{permissionsSummary(role)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap justify-end gap-1">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => onEdit(role)}
                    >
                      Editar
                    </Button>
                    {allowDelete && (
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => onDelete?.(role)}
                      >
                        Eliminar
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
